<template>
	<div v-if="!order.error" class="order" :id="'order_' + order.id">
		<table class="table table-condensed">
			<tbody>
				<tr class="order-id-wrap">
					<td>
						{{ to('ORDER_ID') }}
					</td>
					<td class="id-number">
						{{ order.id }}
					</td>
				</tr>
				<tr>
					<td style="vertical-align: middle">{{ to('ORDER_STATUS') }}</td>
					<td :style="orderStatusStyle">
						<b>{{ orderStatus }}</b>
					</td>
				</tr>
			</tbody>
		</table>
		<!-- If mail order, show deliveries. -->
		<div v-if="order.deliveries && order.shippingMethod.id !== 4 /*Hämta i butik*/">
			<delivery
				v-for="(delivery, index) in order.deliveries"
				:key="index" :order="order"
				:delivery="delivery"
				:showStatuses="showStatuses"
				:number="order.deliveries.length > 1 ? (index + 1) : null" />
		</div>
		<!-- If store pickup order, show all order rows. -->
		<div v-else>
			<order-status-entry-list v-if="showStatuses" :order="order" :statusEntries="order.entries" />
			<div class="panel-body">
				<order-row
					v-for="row in order.rows"
					:row="row"
					:key="row.id"
					:activeStatus="isActive"
				/>
				<order-row
					v-for="row in cancelledOrderRows"
					:row="row"
					:key="row.id"
					:activeStatus="isActive"
					class="cancelled"
				/>
			</div>
		</div>
		<table class='table table-striped table-condensed small'>
			<tbody>
				<tr>
					<td>{{ to('ORDER_DATE') }}</td>
					<td>{{ orderDate }}</td>
				</tr>
				<tr v-if="reservedUntil">
					<td>{{ to('RESERVED_UNTIL') }} </td>
					<td v-if="order.isExtendable"><b>{{ reservedUntil }}</b><a @click="extend"> Förläng med två dagar</a></td>
					<td v-else>{{ reservedUntil }}</td>

				</tr>
				<tr>
					<td>{{ to('PAYMENT_METHOD') }}</td>
					<td style="width: 100%">{{ paymentMethod }}</td>
				</tr>
				<tr>
					<td>{{ to('SHIPPING_METHOD') }}</td>
					<td>
						{{ shippingMethod }} {{ multipleShippingSite }}
						<order-store-changer :order="order" :secret="secret" @update:store="changeStore" v-if="canChangeStore" />
					</td>
				</tr>
				<tr v-if="shippingAddress">
					<td>{{ to('SHIPPING_ADDRESS') }}</td>
					<td><div v-for="addrRow in shippingAddress">{{ addrRow }}</div></td>
				</tr>
				<tr v-if="sentDate">
					<td>{{ to('SENT_AT') }}</td>
					<td>{{ sentDate }}</td>
				</tr>
				<tr v-if="order.trackingNumber">
					<td>{{ to('TRACKING_NUMBER') }}</td>
					<td v-if="order.trackingUrl">
						<a target="_blank" :href="order.trackingUrl">
							{{ order.trackingNumber }}
						</a>
					</td>
					<td v-else>{{ order.trackingNumber }}</td>
				</tr>
				<tr v-if="postnordShipmentId">
					<td>Paketinformation</td>
					<td><a @click="showPostnord = ! showPostnord" v-text="showPostnord ? 'Dölj paket' : 'Visa paket'"></a></td>
				</tr>
				<tr v-show="showPostnord">
					<td class="postnordWidget" colspan="2">
						<div :id="postnordWidgetId"></div>
					</td>
				</tr>
				<tr v-if="receiptPath">
					<td>{{ to('RECEIPT_COPY') }}</td>
					<td><a :href="receiptPath" target="_blank">{{ to('PRINT') }}</a></td>
				</tr>
				<tr v-if="paymentAgreementPath">
					<td>{{ to('PAYMENT_AGREEMENT') }}</td>
					<td><a :href="paymentAgreementPath" target="_blank">{{ to('PRINT') }}</a></td>
				</tr>
				<tr v-if="order.userExperiencePointBoosts && order.userExperiencePointBoosts.length">
					<td>{{ to('BONUS_XP') }}</td>
					<td>
						<div v-for="xle in order.userExperiencePointBoosts">
							<b>{{ xle.description }}:</b>
							{{ (xle.experiencePoints >= 0 ? '+' : '') + xle.experiencePoints }} XP
						</div>
					</td>
				</tr>
				<tr v-if="canCancel">
					<td>{{ to('CANCEL') }}</td>
					<td>
						<modal :hideCloseButton="true">
							<a slot="label">{{ to('CANCEL_ORDER') }}</a>
							<order-cancel-form :order="order" :isPrepaid="isPrepaid" :isRefundedAutomatically="isRefundedAutomatically" @cancel="v => $emit('cancel', v)" />
						</modal>
					</td>
				</tr>
			</tbody>
		</table>
		<div class='panel-body' v-if="order.productKeys.length">
			<b>Produktnycklar</b>
			<div v-for="prod in order.productKeys">
				<div>{{ prod.title }}</div>
				<toggle-element v-if="prod.description && prod.description.length">
					<template slot-scope="props" slot="label">
						<a>{{ props.active ? 'Dölj' : 'Visa' }} info</a>
					</template>
					<div style="margin-bottom: 16px;">{{ prod.description }}</div>
				</toggle-element>
				<div v-for="key in prod.keys">
					<span v-if="key.activatedDate">{{ key.value }}</span>
				</div>
				<div :class="mq.current <= mq.sm ? 'text-center' : ''">
					<div v-for="key in prod.keys" class="mt-4">
						<span v-if="! key.activatedDate" class="btn btn-primary" :style="{ width: mq.current <= mq.sm ? '55%' : '30%' }" style="min-width: 150px" @click="activateKey(key)">Aktivera</span>
					</div>
				</div>
			</div>
		</div>
		<table class='panel-footer order-footer'>
			<tr>
				<td>{{ to('SHIPPING_PRICE') }}:</td>
				<td style="width: 100%">{{ shippingPriceFormatted }}</td>
			</tr>
			<tr>
				<td>{{ to('TOTAL_PRICE') }}:</td>
				<td>
					{{ totalPriceFormatted }}
					<span class="ml-3 small">
						({{ to('OF_WHICH_VAT') }}: {{ totalVatFormatted }})
					</span>
				</td>
			</tr>
			<tr v-if="this.order.discount > 0">
				<td>Rabatt:</td>
				<td>-{{ this.discount }}</td>
			</tr>
			<template v-if="order.payments">
				<tr v-for="payment in order.payments">
					<td>{{ payment.paymentMethod.name }}</td>
					<td>{{ formatPrice(-payment.amount, order.currency) }} <span class="ml-3 small secondary" v-if="payment.reference">({{ payment.reference }})</span></td>
				</tr>
			</template>
			<tr style="border-top: 1px solid #ccc;" v-if="order.paymentMethod.id === 2 /*Betala i butik*/ && pendingAmount < totalPrice">
				<td><b>Att betala:</b></td>
				<td><b>{{ formatPrice(pendingAmount, order.currency) }}</b></td>
			</tr>
		</table>
		<div class="mx-5 pt-5" v-if="isPickupReady && ! hideStoreInfo">
			<store-info-container :store="selectedStore" useColLayout hideExtraInfo />
			<div style="height: 12px" />
		</div>
	</div>
	<div v-else-if="order.id" class="order loading-error" :id="'order_' + order.id">
		<table class="table table-condensed">
			<tbody>
				<tr class="order-id-wrap">
					<td>
						{{ to('ORDER_ID') }}
					</td>
					<td class="id-number">
						{{ order.id }}
					</td>
				</tr>
				<tr>
					<td colspan="2" >
						<div class="alert alert-danger" >
							Vi har problem att ladda order {{ order.id }}, kontakta kundtjänst för support.
						</div>
					</td>
				</tr>
			</tbody>
		</table>
		</div>
</template>

<script>
import waja from 'waja';
import alert from 'alert';
import mq from 'mediaQuery';
import PHPDate from 'PHPDate';
import { formatPrice } from 'utils';
import { mapState } from 'vuex';
import subMonths from 'date-fns/sub_months';
import { t, getStore } from 'datastore/i18n';


// Order status codes.
const STATUS_CANCELLED = 0;
const STATUS_RECEIVED = 1;
const STATUS_PROCESSING = 2;
const STATUS_PICKUP_READY = 3;
const STATUS_DELIVERED = 4;
const STATUS_SERVICE = 5;

const statusPhrases = [
	'CANCELLED',
	'RECEIVED',
	'PROCESSING',
	'PICKUP_READY',
	'DELIVERED',
	'SERVICE',
];


//Postnordgrejer
let postnordWidgetLoading = false;
const postnordWidgetOnLoadCallbacks = [];

function loadPostnordWidget (options = {}) {
	const callback = () => {
		const opts = Object.assign({
			apiKey: 'dcef917f0bb8911f4098f94d763f4b82',
			country: 'se',
			width: 800,
			//height: 800,
		}, options);
		console.log('Load postnord widget', JSON.parse(JSON.stringify(opts)), options);
		window.PostnordWidget.init(opts);
	};

	if (window.PostnordWidget) {
		callback();
		return;
	}

	postnordWidgetOnLoadCallbacks.push(callback);

	if (! postnordWidgetLoading) {
		postnordWidgetLoading = true;
		const scriptTag = document.createElement('script');
		scriptTag.setAttribute('src', 'https://tracking.postnord.com/js/postnord-widget-loader.min.js');

		scriptTag.onload = () => {
			scriptTag.parentNode.removeChild(scriptTag);
			for (const cb of postnordWidgetOnLoadCallbacks) {
				cb();
				return;
			}
		};

		document.head.appendChild(scriptTag);
	}
}
// END postnordgrejer



export default {
	props: {
		order: {},
		noCancel: {
			default: false,
		},
		secret: {
			type: String,
			default: null,
		},
		showStatuses: {
			type: Boolean,
			default: false,
		},
		hideStoreInfo: {
			type: Boolean,
			default: false,
		},
		cancelledOrderRows: {
			type: Array,
			default: () => [],
		},
	},
	data () {
		return {
			mq,
			showPostnord: false
		};
	},
	methods: {
		to: t.prefix('ORDER'),
		t,
		alert,
		formatPrice: formatPrice,
		changeStore (selectedStoreId) {
			const data = {
				order: {
					storeId: selectedStoreId,
				},
			};
			if (this.secret) {
				data.secret = this.secret;
			}
			waja.patch('order/' + this.order.id)
				.data(data)
				.on('success', res => {
					// @TODO egentligen vill vi inte ändra denna direkt, men det funkar for now. Gör detta bättre när vi fixar pagination
					this.order = res.data.order;
					if (res.data.errors && res.data.errors.length && res.data.errors[0] === 'store_already_changed') {
						alert('Du har redan ändrat butik för denna order! Ändringen har inte sparats!');
					} else {
						alert('Ditt val har sparats!');
					}
				}).on('400', res => {
					alert('Du kan inte byta butik på en bindande order!')
				})
				.on('error', res => {
					alert('Något gick fel vid byte av butik!');
				}).go();
		},
		activateKey (key) {
			const req = waja.post('product-key/' + key.id + '/activate')
				.on('success', res => {
					key.value = res.data.key.value;
					key.activatedDate = res.data.key.activatedDate;
					alert('Nyckeln har aktiverats!');
				}).on('error', res => {
					alert('Något gick fel!');
				});
			const secret = this.$route.query.secret;
			if (secret) {
				req.data({ secret });
			}
			req.go();
		},
		extend () {
			const data = {
				order: {
					extendPickup: true,
				},
			};
			if (this.secret) {
				data.secret = this.secret;
			}
			waja.patch('order/' + this.order.id)
				.data(data)
				.on('success', res => {
					this.order = res.data.order;
					if (res.data.errors && res.data.errors.length) {
						alert("Din order har inte ändrats!");
					} else {
						alert("Din order har blivit förlängd!");
					}
				}).on('error', res => {
					alert('Något gick fel vid förlängning av reservationsdatum!');
				}).go();
		},
	},
	mounted () {
		if (this.postnordShipmentId) { // @TODO
			this.$nextTick(() => { // Så att elementet hinner laddas
				loadPostnordWidget({
					shipmentId: this.postnordShipmentId,
					element: '#' + this.postnordWidgetId,
				});
			});
		}
	},
	computed: {
		discount() {
			const currency = 'SEK';
			return formatPrice(this.order.discount, currency);
		},
		rows () {
			if (this.order.rows) {
				return this.order.rows;
			}
			if (this.order.deliveries) {
				return [].concat(...this.order.deliveries.map(delivery => delivery.rows));
			}
			return [];
		},
		pendingAmount () {
			let pending = this.totalPrice;
			if (this.order.payments) {
				for (const payment of this.order.payments) {
					pending -= payment.amount;
				}
			}
			if (this.order.discount > 0) {
				pending -= this.order.discount;
			}
			return pending;
		},
		isPrepaid () {
			// @TODO presentkort / tillgodo är förbetalning
			return this.order.paymentMethod.isPrepayment;
		},
		isRefundedAutomatically() {
			return this.order.paymentMethod.isRefundedAutomatically;
		},
		isActive() {
			return ! (this.order.statusCode === STATUS_CANCELLED || this.order.statusCode === STATUS_DELIVERED);
		},
		canChangeStore () {
			return false; // @TODO Change back when we can update an order in IFS
			if (this.order.shippingMethod.id !== 4 || this.order.isBinding) {
				return false;
			}
			//remove link for change store, if current store = Webhallen Config
			else if (this.selectedStore == 999) {
				return false;
			}
			return this.order.statusCode === STATUS_RECEIVED || this.order.statusCode === STATUS_PICKUP_READY;
		},
		orderStatusStyle () {
			if (this.order.statusCode === STATUS_PICKUP_READY) {
				return 'color: #1a941a';
			}
			return null;
		},
		postnordShipmentId () {
			if (this.order.shippingMethod?.id !== 1) { // Brev/postpaket
				return null;
			}
			const sentDate = new Date(this.order.sentDate * 1000);
			const oldestToLoad = subMonths(sentDate, 1);
			if (sentDate < oldestToLoad) {
				return null; // ingen widget om ordern skickades för mer än ett halvår sedan
			}
			return this.order.trackingNumber;
		},
		postnordWidgetId () {
			return 'postnord-widget-container-' + this._uid;
		},
		canCancel () {
			if (this.noCancel !== false || this.order.isBinding) {
				return false;
			}
			if (this.order.hasOwnProperty('canCancelOrder') && ! this.order.canCancelOrder) {
				return false;
			}
			return this.order.statusCode === STATUS_RECEIVED || this.order.statusCode === STATUS_PICKUP_READY;
		},
		shippingAddress () {
			if (! this.order.shippingAddress) return;
			const addr = this.order.shippingAddress;
			const rows = [];
			rows.push(addr.name);
			rows.push(addr.address);
			rows.push(addr.zipCode + ' ' + addr.city);
			return rows;
		},
		orderStatus () {
			return this.to('STATUS_' + statusPhrases[this.order.statusCode]);
		},
		/**
		 * Only true if pickup shipping method is used and the order is ready to pickup.
		 */
		isPickupReady () {
			return this.order.statusCode === STATUS_PICKUP_READY;
		},
		selectedStore () {
			const store = this.order.store ? getStore(this.order.store.id) : null;
			return store;
		},
		orderDate () {
			const date = PHPDate('Y-m-d H:i', this.order.orderDate);
			return date;
		},
		reservedUntil () {
			const date = this.order.reservedUntil;
			if (date) {
				return PHPDate('Y-m-d H:i', date);
			}
			return null;
		},
		sentDate () {
			if (! (this.order.statusCode === 4 /* hämtad/skickad */ && this.order.sentDate)) {
				return false;
			}
			const date = PHPDate('Y-m-d H:i', this.order.sentDate);
			return date;
		},
		paymentMethod () {
			return this.order.paymentMethod.name;
		},
		shippingMethod () {
			let out = this.order.shippingMethod.name;
			if (this.order.shippingMethod.id === 4 /* Hämta i butik */ && this.order.store) {
				out = out + ' - ' + this.order.store.name + ', ' + this.order.store.address;
			}
			return out;
		},
		multipleShippingSite () {
			let string = '';
			let stores = {};
			if (this.order.shippingMethod.id !== 4 /* Hämta i butik */) {
				for (const i in this.rows) {
					stores[this.rows[i].storeId] = this.rows[i].storeId;
				}
				if (Object.keys(stores).length > 1) {
					string = '(Skickas ifrån olika lager)';
				}
			}
			return string;
		},
		totalPrice () { // @TODO valuta på priset från vart???
			let sum = this.order.shippingMethod.price;
			for (const i in this.rows) {
				const row = this.rows[i];
				sum += row.unitPrice * row.quantity;
				if (row.insuranceInfo && row.insuranceInfo.insured) {
					sum += row.insuranceInfo.unitPrice * row.insuranceInfo.quantity;
				}
			}
			return sum;
		},
		totalVat () {
			let sum = this.order.shippingMethod.vat;
			for (const i in this.rows) {
				const row = this.rows[i];
				sum += row.unitVat * row.quantity;
			} // @TODO räkna på moms från frakten också
			return Math.round(sum);
		},
		totalPriceFormatted () {
			const currency = 'SEK';
			return formatPrice(this.totalPrice, currency);
		},
		shippingPriceFormatted () {
			const currency = 'SEK';
			return formatPrice(this.order.shippingMethod.price, currency);
		},
		totalVatFormatted () {
			const currency = 'SEK';
			return formatPrice(this.totalVat, currency);
		},
		receiptPath () { // @TODO conditions
			const status = this.order.statusCode;
			if (status !== STATUS_DELIVERED) {
				return null;
			}
			let url = '/api/receipt/' + this.order.id;
			if (this.secret) {
				url += '?secret=' + this.secret;
			}
			return url;
		},
		paymentAgreementPath () {
			if (this.order.paymentMethod.id !== 22) {
				return false;
			}
			return 'https://classic.webhallen.com/print_webbis_avtal.php?order_id=' + this.order.id;
		},
		...mapState ({
			ranklevel:state => state.user.rankLevel,
		}),
	},
};
</script>
